import { useState, useEffect, useRef, useMemo } from 'react';
import { ChevronDown, Search, Settings2, ArrowLeft } from 'lucide-react';
import { useFontStore } from '../../stores/fontStore';
import { FontManager } from './FontManager';
import type { CustomFont } from '../../lib/fontLoader';

export interface FontVariantSelection {
  variantId: string;
  fontFamily: string;
  fontWeight: number;
  fontStyle: 'normal' | 'italic';
}

interface FontPickerProps {
  /** Current variant id, or undefined for legacy elements that have no variant id yet. */
  variantId?: string;
  /** Current family name (used as fallback label when no variant id is set). */
  family: string;
  onSelectVariant: (variant: FontVariantSelection) => void;
}

function variantLabel(v: CustomFont): string {
  const base = v.displayName && v.displayName.trim().length > 0
    ? v.displayName.replace(new RegExp(`^${v.fontFamily}\\s*`, 'i'), '').trim()
    : '';
  if (base) return base || 'Regular';
  const weight = parseInt(v.fontWeight, 10) || 400;
  const style = v.fontStyle === 'italic' ? ' Italic' : '';
  const weightName =
    weight <= 100 ? 'Thin'
    : weight <= 200 ? 'Extra Light'
    : weight <= 300 ? 'Light'
    : weight <= 400 ? 'Regular'
    : weight <= 500 ? 'Medium'
    : weight <= 600 ? 'Semi Bold'
    : weight <= 700 ? 'Bold'
    : weight <= 800 ? 'Extra Bold'
    : 'Black';
  return `${weightName}${style}`;
}

export default function FontPicker({ variantId, family, onSelectVariant }: FontPickerProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [familyView, setFamilyView] = useState<string | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const { fonts, isManagerOpen, openManager, closeManager, refreshFonts } = useFontStore();

  useEffect(() => {
    if (isOpen) refreshFonts();
  }, [isOpen]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsOpen(false);
        setSearch('');
        setFamilyView(null);
      }
    };
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      requestAnimationFrame(() => searchRef.current?.focus());
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  const familyGroups = useMemo(() => {
    const map = new Map<string, CustomFont[]>();
    for (const font of fonts) {
      if (!font.isActive) continue;
      const list = map.get(font.fontFamily) || [];
      list.push(font);
      map.set(font.fontFamily, list);
    }
    for (const [, list] of map) {
      list.sort((a, b) => {
        const wa = parseInt(a.fontWeight, 10) || 400;
        const wb = parseInt(b.fontWeight, 10) || 400;
        if (wa !== wb) return wa - wb;
        if (a.fontStyle === b.fontStyle) return 0;
        return a.fontStyle === 'italic' ? 1 : -1;
      });
    }
    return map;
  }, [fonts]);

  const fontFamilies = useMemo(() => Array.from(familyGroups.keys()).sort(), [familyGroups]);

  const filteredFamilies = search.trim()
    ? fontFamilies.filter((f) => f.toLowerCase().includes(search.toLowerCase()))
    : fontFamilies;

  const currentVariant = variantId ? fonts.find((f) => f.id === variantId) : undefined;
  const buttonLabel = currentVariant
    ? `${currentVariant.fontFamily} — ${variantLabel(currentVariant)}`
    : family || 'Select font';

  const openFamily = familyView;
  const openFamilyVariants = openFamily ? familyGroups.get(openFamily) || [] : [];

  const handlePickVariant = (v: CustomFont) => {
    onSelectVariant({
      variantId: v.id,
      fontFamily: v.fontFamily,
      fontWeight: parseInt(v.fontWeight, 10) || 400,
      fontStyle: v.fontStyle === 'italic' ? 'italic' : 'normal',
    });
    setIsOpen(false);
    setSearch('');
    setFamilyView(null);
  };

  return (
    <>
      <div className="relative" ref={dropdownRef}>
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="w-full flex items-center justify-between px-2 py-1.5 bg-[#0a0a0a] border border-white/10 rounded-lg text-xs text-zinc-200 hover:border-white/20 transition-colors"
        >
          <span
            style={{ fontFamily: `"${currentVariant?.fontFamily || family}", sans-serif` }}
            className="truncate"
          >
            {buttonLabel}
          </span>
          <ChevronDown size={12} className="text-zinc-500 flex-shrink-0 ml-2" />
        </button>

        {isOpen && (
          <div className="absolute z-50 top-full left-0 right-0 mt-1 bg-[#0d0d12] border border-white/[0.1] rounded-lg shadow-2xl max-h-[360px] overflow-hidden flex flex-col animate-fade-in">
            <div className="p-1.5 border-b border-white/[0.06] flex items-center gap-1.5">
              {openFamily ? (
                <button
                  onClick={() => setFamilyView(null)}
                  className="p-1 rounded hover:bg-white/[0.06] text-zinc-400 hover:text-cyan-400 transition-colors"
                  title="Back to families"
                >
                  <ArrowLeft size={13} />
                </button>
              ) : null}
              <div className="relative flex-1">
                <Search size={11} className="absolute left-2 top-1/2 -translate-y-1/2 text-zinc-600" />
                <input
                  ref={searchRef}
                  type="text"
                  placeholder={openFamily ? `Search ${openFamily} variants...` : 'Search fonts...'}
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="w-full pl-6 pr-2 py-1 text-xs bg-white/[0.04] border border-white/[0.06] rounded text-white placeholder-zinc-600 focus:outline-none focus:border-cyan-500/40"
                />
              </div>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setIsOpen(false);
                  setSearch('');
                  setFamilyView(null);
                  openManager();
                }}
                className="p-1 rounded hover:bg-white/[0.06] text-zinc-500 hover:text-cyan-400 transition-colors flex-shrink-0"
                title="Manage Fonts"
              >
                <Settings2 size={14} />
              </button>
            </div>

            <div className="overflow-y-auto flex-1 sidebar-scroll">
              {!openFamily && (
                <>
                  {filteredFamilies.length > 0 && (
                    <div>
                      <div className="px-2.5 py-1 text-[9px] uppercase tracking-wider font-semibold sticky top-0 bg-[#0d0d12] text-cyan-500">
                        Families
                      </div>
                      {filteredFamilies.map((fam) => {
                        const variants = familyGroups.get(fam) || [];
                        return (
                          <button
                            key={fam}
                            onClick={() => {
                              if (variants.length === 1) {
                                handlePickVariant(variants[0]);
                              } else {
                                setFamilyView(fam);
                                setSearch('');
                              }
                            }}
                            className={`w-full text-left px-2.5 py-1.5 text-xs hover:bg-white/[0.04] transition-colors flex items-center justify-between gap-2 ${
                              currentVariant?.fontFamily === fam
                                ? 'bg-cyan-500/10 text-cyan-400'
                                : 'text-zinc-300'
                            }`}
                          >
                            <span
                              style={{ fontFamily: `"${fam}", sans-serif` }}
                              className="truncate"
                            >
                              {fam}
                            </span>
                            <span className="text-[9px] text-zinc-500 flex-shrink-0 bg-white/[0.04] px-1.5 rounded">
                              {variants.length} {variants.length === 1 ? 'variant' : 'variants'}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  )}
                  {filteredFamilies.length === 0 && (
                    <div className="px-3 py-6 text-center">
                      {search.trim() ? (
                        <p className="text-xs text-zinc-600">No fonts match &quot;{search}&quot;</p>
                      ) : (
                        <div className="space-y-2">
                          <p className="text-xs text-zinc-500">No fonts uploaded yet.</p>
                          <p className="text-[11px] text-zinc-600">
                            Click the <Settings2 size={10} className="inline text-zinc-500" /> gear icon to open Font Manager.
                          </p>
                        </div>
                      )}
                    </div>
                  )}
                </>
              )}

              {openFamily && (
                <div>
                  <div className="px-2.5 py-1 text-[9px] uppercase tracking-wider font-semibold sticky top-0 bg-[#0d0d12] text-cyan-500">
                    {openFamily} Variants
                  </div>
                  {openFamilyVariants
                    .filter((v) =>
                      search.trim()
                        ? variantLabel(v).toLowerCase().includes(search.toLowerCase())
                        : true
                    )
                    .map((v) => {
                      const label = variantLabel(v);
                      const isCurrent = currentVariant?.id === v.id;
                      return (
                        <button
                          key={v.id}
                          onClick={() => handlePickVariant(v)}
                          className={`w-full text-left px-2.5 py-1.5 text-xs hover:bg-white/[0.04] transition-colors flex items-center justify-between gap-2 ${
                            isCurrent ? 'bg-cyan-500/10 text-cyan-400' : 'text-zinc-300'
                          }`}
                        >
                          <span
                            style={{
                              fontFamily: `"${v.fontFamily}", sans-serif`,
                              fontWeight: parseInt(v.fontWeight, 10) || 400,
                              fontStyle: v.fontStyle === 'italic' ? 'italic' : 'normal',
                            }}
                            className="truncate"
                          >
                            {label}
                          </span>
                          <span className="text-[9px] text-zinc-500 flex-shrink-0 bg-white/[0.04] px-1.5 rounded">
                            {parseInt(v.fontWeight, 10) || 400}
                            {v.fontStyle === 'italic' ? ' · italic' : ''}
                          </span>
                        </button>
                      );
                    })}
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      <FontManager isOpen={isManagerOpen} onClose={closeManager} />
    </>
  );
}
